import * as Sentry from "@sentry/browser";
import { Operation, UndoOrRedo } from "./Operation";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeStore } from "../ShapeStore";

export interface WBOperationManagerState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
}

export class OperationManager {
  shapeStore: ShapeStore;
  undoStack: Operation[] = [];
  redoStack: Operation[] = [];
  onStateChanged: (state: WBOperationManagerState) => void;

  constructor(
    shapeStore: ShapeStore,
    onStateChanged: (state: WBOperationManagerState) => void
  ) {
    this.shapeStore = shapeStore;
    this.onStateChanged = onStateChanged;
  }

  get state(): WBOperationManagerState {
    return {
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoLabel: this.undoStack[this.undoStack.length]?.label ?? null,
      redoLabel: this.redoStack[this.redoStack.length]?.label ?? null,
    };
  }

  popInvalidOperations() {
    while (
      this.undoStack.length &&
      !this.undoStack[this.undoStack.length - 1].getIsValid(
        UndoOrRedo.Undo,
        this.shapeStore
      )
    ) {
      console.log(
        "Invalidating undo operation",
        this.undoStack[this.undoStack.length - 1]
      );
      this.undoStack.pop();
    }

    if (
      this.redoStack.length &&
      !this.redoStack[this.redoStack.length - 1].getIsValid(
        UndoOrRedo.Redo,
        this.shapeStore
      )
    ) {
      console.log("Invalidating redo stack");
      this.redoStack = [];
    }

    this.onStateChanged(this.state);
  }

  get nextUndoOperation(): Operation | null {
    if (!this.undoStack.length) return null;
    return this.undoStack[this.undoStack.length - 1];
  }

  get nextRedoOperation(): Operation | null {
    if (!this.redoStack.length) return null;
    return this.redoStack[this.redoStack.length - 1];
  }

  addOperation(op: Operation) {
    this.undoStack.push(op);
    this.redoStack = [];
    this.onStateChanged(this.state);
  }

  undo(wb: WhiteboardControllerInterface): boolean {
    this.popInvalidOperations();
    const op = this.undoStack.pop();
    if (!op) return false;

    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: `undoOperation-${op.constructor.name}`,
      level: Sentry.Severity.Info,
    });

    op.applyBackward(wb.shapeStore);
    this.redoStack.push(op);
    this.onStateChanged(this.state);
    return true;
  }

  redo(wb: WhiteboardControllerInterface): boolean {
    this.popInvalidOperations();
    const op = this.redoStack.pop();
    if (!op) return false;

    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: `redoOperation-${op.constructor.name}`,
      level: Sentry.Severity.Info,
    });

    op.applyForward(wb.shapeStore);
    this.undoStack.push(op);
    this.onStateChanged(this.state);
    return true;
  }

  reset() {
    this.undoStack = [];
    this.onStateChanged(this.state);
  }
}
